import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, History, Loader2 } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { notificar } from '@/shared/ui/avisos';
import { ResumenDeAuditoria, type Resumen } from './ResumenDeAuditoria';
import { accionDe, ACCIONES, moduloDe } from './rotulos';

interface Registro {
  id: number;
  autor: string | null;
  modulo: string;
  accion: string;
  descripcion: string;
  entidad: string | null;
  entidadId: number | null;
  ip: string | null;
  ocurridoEn: string;
}

interface Filtros {
  modulos: string[];
  acciones: string[];
  autores: string[];
}

const RANGOS = [
  { dias: 0, etiqueta: 'Hoy' },
  { dias: 7, etiqueta: '7 días' },
  { dias: 30, etiqueta: '30 días' },
] as const;

function haceDias(dias: number): string {
  const fecha = new Date();
  fecha.setDate(fecha.getDate() - dias);
  return enIso(fecha);
}

function enIso(fecha: Date): string {
  const dosCifras = (n: number) => String(n).padStart(2, '0');
  return `${fecha.getFullYear()}-${dosCifras(fecha.getMonth() + 1)}-${dosCifras(fecha.getDate())}`;
}

export function PaginaAuditoria() {
  const [buscar, setBuscar] = useState('');
  const [modulo, setModulo] = useState('');
  const [accion, setAccion] = useState('');
  const [autor, setAutor] = useState('');
  const [desde, setDesde] = useState(haceDias(7));
  const [hasta, setHasta] = useState(haceDias(0));
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [bajando, setBajando] = useState(false);

  const filtro = { buscar, modulo, accion, autor, desde, hasta };

  const filtros = useQuery({
    queryKey: ['auditoria-filtros'],
    queryFn: () => api.get<Filtros>('/auditoria/filtros'),
  });

  const resumen = useQuery({
    queryKey: ['auditoria-resumen', filtro],
    queryFn: () => api.get<Resumen>('/auditoria/resumen', { query: filtro }),
  });

  const registros = useQuery({
    queryKey: ['auditoria', filtro, pagina, tamanio],
    queryFn: () => api.get<Pagina<Registro>>('/auditoria', { query: { ...filtro, pagina, tamanio } }),
  });

  function alFiltrar(poner: (valor: string) => void) {
    return (valor: string) => {
      poner(valor);
      setPagina(0);
    };
  }

  function ponerRango(dias: number) {
    setDesde(haceDias(dias));
    setHasta(haceDias(0));
    setPagina(0);
  }

  const rangoActivo = RANGOS.find((r) => desde === haceDias(r.dias) && hasta === haceDias(0));

  async function exportar() {
    setBajando(true);
    try {
      const parametros = new URLSearchParams(filtro);
      const fichero = await api.descargar(`/auditoria/exportacion.csv?${parametros}`);

      if (!fichero) {
        notificar.error('NO SE PUDO ARMAR LA EXPORTACIÓN');
        return;
      }

      const url = URL.createObjectURL(fichero);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `auditoria-${desde}-a-${hasta}.csv`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo exportar');
    } finally {
      setBajando(false);
    }
  }

  const columnas: Columna<Registro>[] = [
    {
      clave: 'cuando',
      encabezado: 'Hora',
      clase: 'w-20',
      contenido: (r) => (
        <span className="block whitespace-nowrap font-mono text-xs font-semibold tabular-nums text-armazon-700">
          {enHora(r.ocurridoEn)}
        </span>
      ),
    },
    {
      clave: 'quien',
      encabezado: 'Quién',
      contenido: (r) => <Persona correo={r.autor} />,
    },
    {
      clave: 'accion',
      encabezado: 'Qué hizo',
      contenido: (r) => {
        const { texto, icono: Icono, tono } = accionDe(r.accion);
        return (
          <span className="flex items-center gap-2.5">
            <span className={`grid size-7 shrink-0 place-items-center rounded-lg ${tono}`}>
              <Icono className="size-[15px]" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block whitespace-nowrap text-xs font-semibold text-armazon-700">
                {texto}
              </span>
              <span className="block text-[10px] uppercase tracking-wide text-armazon-400">
                {moduloDe(r.modulo)}
              </span>
            </span>
          </span>
        );
      },
    },
    {
      clave: 'descripcion',
      encabezado: 'Detalle',
      contenido: (r) => (
        <span className="block">
          <span className="block text-[13px] leading-relaxed text-armazon-700">
            {r.descripcion}
          </span>
          {r.entidad && (
            <span className="mt-0.5 inline-block rounded bg-armazon-100 px-1.5 py-0.5 font-mono text-[10px] text-armazon-500">
              {r.entidad} #{r.entidadId}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'ip',
      encabezado: 'Desde',
      alinear: 'derecha',
      contenido: (r) => (
        <span className="whitespace-nowrap font-mono text-[11px] text-armazon-400">
          {r.ip ?? '—'}
        </span>
      ),
    },
  ];

  return (
    <>
      <EncabezadoDePagina
        icono={History}
        color="pizarra"
        titulo="Auditoría"
        subtitulo="Qué pasó, quién lo hizo y cuándo"
        acciones={
          <button
            type="button"
            onClick={() => void exportar()}
            disabled={bajando || !resumen.data?.total}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-armazon-200 bg-white px-4 text-[11px] font-bold uppercase tracking-wide text-armazon-600 transition hover:border-armazon-300 disabled:opacity-40"
          >
            {bajando ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Download className="size-4" aria-hidden />
            )}
            Exportar
          </button>
        }
      />

      <ResumenDeAuditoria resumen={resumen.data} />

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-armazon-200 bg-white p-4">
        <CampoDeBusqueda
          valor={buscar}
          alCambiar={alFiltrar(setBuscar)}
          marcador="Buscar en el detalle…"
          className="min-w-[200px] flex-1"
        />

        <Selector
          valor={modulo}
          alCambiar={alFiltrar(setModulo)}
          todos="Todos los módulos"
          opciones={(filtros.data?.modulos ?? []).map((m) => ({
            valor: m,
            etiqueta: moduloDe(m),
          }))}
        />

        <Selector
          valor={accion}
          alCambiar={alFiltrar(setAccion)}
          todos="Cualquier acción"
          opciones={(filtros.data?.acciones ?? []).map((a) => ({
            valor: a,
            etiqueta: ACCIONES[a]?.texto ?? a,
          }))}
        />

        <Selector
          valor={autor}
          alCambiar={alFiltrar(setAutor)}
          todos="Cualquier persona"
          opciones={(filtros.data?.autores ?? []).map((a) => ({ valor: a, etiqueta: a }))}
        />

        <span className="flex w-full flex-wrap items-center gap-3 border-t border-armazon-100 pt-3">
          <span className="flex gap-1">
            {RANGOS.map((r) => (
              <button
                key={r.dias}
                type="button"
                onClick={() => ponerRango(r.dias)}
                aria-pressed={rangoActivo?.dias === r.dias}
                className={`h-9 rounded-lg border px-3 text-[11px] font-semibold uppercase transition ${
                  rangoActivo?.dias === r.dias
                    ? 'border-marca-400 bg-marca-50 text-marca-700'
                    : 'border-armazon-200 bg-white text-armazon-500 hover:border-armazon-300'
                }`}
              >
                {r.etiqueta}
              </button>
            ))}
          </span>

          <span className="flex items-center gap-2">
            <input
              type="date"
              value={desde}
              max={hasta}
              onChange={(evento) => alFiltrar(setDesde)(evento.target.value)}
              aria-label="Desde"
              className="h-9 rounded-lg border border-armazon-200 px-2 text-xs text-armazon-700"
            />
            <span className="text-[11px] uppercase text-armazon-400">a</span>
            <input
              type="date"
              value={hasta}
              min={desde}
              onChange={(evento) => alFiltrar(setHasta)(evento.target.value)}
              aria-label="Hasta"
              className="h-9 rounded-lg border border-armazon-200 px-2 text-xs text-armazon-700"
            />
          </span>
        </span>
      </div>

      <Tabla
        datos={registros.data}
        cargando={registros.isLoading}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(nuevo) => {
          setTamanio(nuevo);
          setPagina(0);
        }}

        separador={(fila, anterior) =>
          !anterior || enDia(fila.ocurridoEn) !== enDia(anterior.ocurridoEn)
            ? enDiaLargo(fila.ocurridoEn)
            : null
        }
        iconoVacio={History}
        mensajeVacio="Nada registrado con estos filtros"
        entidad="REGISTROS"
      />
    </>
  );
}

function Persona({ correo }: { correo: string | null }) {
  const quien = correo ?? 'sistema';
  const iniciales = quien.slice(0, 2).toUpperCase();

  return (
    <span className="flex items-center gap-2">
      <span
        className={`grid size-7 shrink-0 place-items-center rounded-full text-[10px] font-bold ${
          correo ? 'bg-marca-50 text-marca-700' : 'bg-armazon-100 text-armazon-400'
        }`}
        aria-hidden
      >
        {iniciales}
      </span>
      <span className="block max-w-[190px] truncate text-xs text-armazon-600" title={quien}>
        {quien}
      </span>
    </span>
  );
}

function Selector({
  valor,
  alCambiar,
  todos,
  opciones,
}: {
  valor: string;
  alCambiar: (valor: string) => void;
  todos: string;
  opciones: { valor: string; etiqueta: string }[];
}) {
  return (
    <select
      value={valor}
      onChange={(evento) => alCambiar(evento.target.value)}
      aria-label={todos}
      className={`h-9 rounded-lg border bg-white px-2 text-xs transition ${
        valor
          ? 'border-marca-400 text-marca-700'
          : 'border-armazon-200 text-armazon-700'
      }`}
    >
      <option value="">{todos}</option>
      {opciones.map((o) => (
        <option key={o.valor} value={o.valor}>
          {o.etiqueta}
        </option>
      ))}
    </select>
  );
}

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

function enHora(iso: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) {
    return iso;
  }
  const dosCifras = (n: number) => String(n).padStart(2, '0');
  return `${dosCifras(fecha.getHours())}:${dosCifras(fecha.getMinutes())}`;
}

function enDia(iso: string): string {
  const fecha = new Date(iso);
  return Number.isNaN(fecha.getTime()) ? iso : enIso(fecha);
}

function enDiaLargo(iso: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) {
    return iso;
  }

  const dia = enIso(fecha);
  const prefijo = dia === haceDias(0) ? 'Hoy · ' : dia === haceDias(1) ? 'Ayer · ' : '';

  return (
    `${prefijo}${DIAS[fecha.getDay()]}, ${fecha.getDate()} de ${MESES[fecha.getMonth()]}` +
    ` de ${fecha.getFullYear()}`
  );
}
